import { useEffect, useRef, useState } from "react";

import { FullscriptCategory } from "@shared/components/CatalogSearch/Filters/FullscriptCategory/FullscriptCategory";
import {
  useCatalogContextualFiltersV2SharedQueryLazyQuery,
  type CatalogContextualFiltersV2SharedQuery,
} from "@shared/data/queries/CatalogContextualFilters/CatalogContextualFiltersV2.query.generated";
import { useCatalogHideBrandsCheck } from "@shared/hooks/useCatalogHideBrandsCheck/useCatalogHideBrandsCheck";
import { catalogTypeVar } from "@shared/reactiveVar/useCatalogType/useCatalogType.store";
import { useSearchSessionIdVar } from "@shared/reactiveVar/useSearchSessionId/useSearchSessionId.store";
import type { OmitEmptyObject } from "@shared/types/OmitEmptyObject";

import { Allergens } from "../Filters/Allergens/Allergens";
import { Availability } from "../Filters/Availability/Availability";
import { Brands } from "../Filters/Brands/Brands";
import { Certifications } from "../Filters/Certifications/Certifications";
import { CurrentOffers } from "../Filters/CurrentOffers/CurrentOffers";
import { Demographics } from "../Filters/Demographics/Demographics";
import { Favorites } from "../Filters/Favorites/Favorites";
import { Form } from "../Filters/Form/Form";
import { ExcludeIngredients } from "../Filters/Ingredients/ExcludeIngredients/ExcludeIngredients";
import { IncludeIngredients } from "../Filters/Ingredients/IncludeIngredients/IncludeIngredients";

interface Props {
  isDrawer: boolean;
  isBold: boolean;
  isCyberMondayEnabled: boolean;
  logSearchApplyFilterEvent: (entityName: string, entityType: string) => void;
}

const PatientFiltersContent = ({
  isDrawer,
  isBold,
  isCyberMondayEnabled,
  logSearchApplyFilterEvent,
}: Props) => {
  const catalogType = catalogTypeVar();
  const isUnauthenticated = catalogType.type === "UnAuth";

  const currentSearchSessionId = useRef<string>("");
  const searchSessionId = useSearchSessionIdVar();

  const [contextualDataCacheV2, setContextualDataCacheV2] = useState<
    | OmitEmptyObject<CatalogContextualFiltersV2SharedQuery["viewer"]>["contextualFilters"]
    | undefined
  >(undefined);

  const [
    getCatalogContextualFiltersV2,
    { data: contextualFilterDataV2, loading: contextualFilterLoadingV2 },
  ] = useCatalogContextualFiltersV2SharedQueryLazyQuery();

  useEffect(() => {
    if (!contextualFilterLoadingV2 && contextualFilterDataV2?.viewer?.contextualFilters) {
      setContextualDataCacheV2(contextualFilterDataV2?.viewer?.contextualFilters);
    }
  }, [contextualFilterDataV2, contextualFilterLoadingV2]);

  useEffect(() => {
    if (searchSessionId !== null && currentSearchSessionId.current !== searchSessionId) {
      currentSearchSessionId.current = searchSessionId;

      void getCatalogContextualFiltersV2({
        variables: {
          searchSessionId,
          dosageFormatSort: [{ field: "SEARCH_POPULARITY", direction: "DESC" }],
          allergenSort: [{ field: "SEARCH_POPULARITY", direction: "DESC" }],
          populationSort: [{ field: "SEARCH_POPULARITY", direction: "DESC" }],
          thirdPartyCertificationSort: [{ field: "SEARCH_POPULARITY", direction: "DESC" }],
        },
      });
    }
  }, [searchSessionId]);

  const shouldHideBrands = useCatalogHideBrandsCheck();

  return (
    <>
      <FullscriptCategory isBold={isBold} />
      <CurrentOffers
        isBold={isBold}
        isDrawer={isDrawer}
        isCyberMondayEnabled={isCyberMondayEnabled}
      />
      <Favorites isBold={isBold} logFilterEvent={logSearchApplyFilterEvent} />
      {!shouldHideBrands && (
        <Brands
          isBold={isBold}
          logFilterEvent={logSearchApplyFilterEvent}
          isDefaultExpanded
          isContextual={!isUnauthenticated}
          contextualData={contextualDataCacheV2?.brands ?? []}
        />
      )}
      <IncludeIngredients
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={!isUnauthenticated}
      />
      <ExcludeIngredients
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={!isUnauthenticated}
      />
      <Form
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={!isUnauthenticated}
        contextualData={contextualDataCacheV2?.forms ?? []}
      />
      <Allergens
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={!isUnauthenticated}
        contextualData={contextualDataCacheV2?.allergens ?? []}
      />
      <Availability isBold={isBold} logFilterEvent={logSearchApplyFilterEvent} />
      <Demographics
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={!isUnauthenticated}
        contextualData={contextualDataCacheV2?.demographics ?? []}
      />
      <Certifications
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={!isUnauthenticated}
        contextualData={contextualDataCacheV2?.thirdPartyCertifications ?? []}
      />
    </>
  );
};

export { PatientFiltersContent };
